import stgConfig from "../../../config";
const ENV_DEV = "dev";
const ENV_STAGING = "staging";
const ENV_PRODUCTION = "production";
const ENV_AUTHOR = "author";

export default class Environment {
  static getWindowLocation() {
    return window.location;
  }

  static getSubdomain() {
    const urlParts = this.getWindowLocation().hostname.split(".");

    if (urlParts[1] === "dev") {
      return "localhost";
    }

    return urlParts[0];
  }

  static getSubdirectory() {
    const urlParts = this.getWindowLocation().pathname.split("/");
    if (urlParts[1] === "wac") {
      return urlParts.length > 1 ? urlParts[2] : "";
    } else {
      return urlParts[1];
    }
  }

  static isStagingPublicEnvironment() {
    return (
      this.getWindowLocation().hostname.indexOf("wac.stg.internal.atlassian") >
      -1 || this.getWindowLocation().hostname.indexOf("qa-wac.atlassian") > -1
    );
  }

  static getEnvironment() {
    const forceStaging = window.location.search.includes('forceProdTesting=true');
    
    if (forceStaging) {
      return ENV_PRODUCTION;
    }
    switch (this.getSubdomain()) {
      case "wac":
      case "qa-wac":
      case "author":
      case "proof":
        return ENV_STAGING;
      case "localhost":
      case "local":
        return ENV_DEV;
      case "truth":
      case "www":
      case "atlassian":
      case "bitbucket":
      case "sourcetreeapp":
      case "agile": 
      case "git":
      case "software":
        return ENV_PRODUCTION;
      default:
        /* for omlet instances */
        return ENV_STAGING;
    }
  }

  static getInternationalSubdomain() {
    const hostnameSubdomain = this.getSubdomain();
    const validLocales = [
      "cn",
      "cs",
      "de",
      "es",
      "fr",
      "it",
      "ko",
      "hu",
      "nl",
      "ja",
      "pl",
      "br",
      "ro",
      "fi",
      "ru",
    ];

    if (validLocales.indexOf(hostnameSubdomain) === -1) {
      return false;
    }

    return hostnameSubdomain;
  }

  static getInternationalSubdirectory() {
    const subdirectory = this.getSubdirectory();
    const validLocales = [
      "zh",
      "cs",
      "de",
      "es",
      "fr",
      "it",
      "ko",
      "hu",
      "nl",
      "ja",
      "pl",
      "br",
      "ro",
      "fi",
      "ru",
    ];

    if (validLocales.indexOf(subdirectory) === -1) {
      return false;
    }

    return subdirectory;
  }

  static isInternationalSubdomain() {
    return !!this.getInternationalSubdomain();
  }

  static production() {
    return ENV_PRODUCTION;
  }

  static staging() {
    return ENV_STAGING;
  }

  static dev() {
    return ENV_DEV;
  }

  static author() {
    return ENV_AUTHOR;
  }

  static isProduction() {
    return this.getEnvironment() === this.production();
  }

  static isStaging() {
    return this.getEnvironment() === this.staging();
  }

  static isDev() {
    return this.getEnvironment() === this.dev();
  }

  static isAuthor() {
    return this.getSubdomain() === this.author();
  }

  static getIdentityBaseUrl() {
    if (this.isStaging() || this.isDev()) {
      return "https://id.stg.internal.atlassian.com";
    } else {
      return "https://id.atlassian.com";
    }
  }

  static getLoginUrl(skipEncoding: boolean) {
    const continueUrl = this.getWindowLocation().href;
    if (!skipEncoding) {
      return `${this.getIdentityBaseUrl()}/login?continue=${encodeURIComponent(
        continueUrl
      )}`;
    } else {
      return `${this.getIdentityBaseUrl()}/login?continue=${continueUrl}`;
    }
  }

  static getLogoutUrl() {
    const continueUrl = this.getWindowLocation().href;
    return `${this.getIdentityBaseUrl()}/logout?continue=${encodeURIComponent(
      continueUrl
    )}`;
  }

  static getSwitchAccountURL(continueUrlParam?: string) {
    const continueUrl = continueUrlParam ? continueUrlParam : this.getWindowLocation().href;
    return `${this.getIdentityBaseUrl()}/login/select-account?continue=${encodeURIComponent(
      continueUrl
    )}`;
  }
  
  static getManageProfileUrl(userEmail: string) {
    const applicationParam = encodeURIComponent("wac")
    const continueUrlParam = encodeURIComponent(`${this.getIdentityBaseUrl()}/manage-profile`)
    const loginHintParam = encodeURIComponent(userEmail)
    
    return `${this.getIdentityBaseUrl()}/login?application=${applicationParam}&continue=${continueUrlParam}&login_hint=${loginHintParam}`;
  }

  static getRelativePathFromWacUrl = (url: string): string => {
    try {
        const urlObj = new URL(url);
        let pathname = urlObj.pathname.replace(/\/+$/, "");
  
        if (pathname.startsWith("/wac/")) {
            pathname = pathname.slice("/wac".length);
        }
        return pathname.replace(/^\/+/, "");
    } catch {
        const match = /\/\/[^/]+(\/[^#?]*)/.exec(url);
        if (match?.[1]) {
            let pathname = match[1].replace(/\/+$/, "");
            if (pathname.startsWith("/wac/")) {
                pathname = pathname.slice("/wac".length);
            }
            return pathname.replace(/^\/+/, "");
        }
        return "";
    }
  };

  static PATH_TO_CONTACT_FLOW_ID_PRODUCTION: Record<string, string> = {
    "software/jira/product-discovery":
        "4cdb6e29-9088-4404-bbfe-dfdba35bd3d6",
    "software/jira":
        "7e776619-ede4-4975-a194-38c6b182fa0c",
    "software/loom":
        "42cee94e-c0db-43d2-bd45-3cca9d7adb61"
  };

  
  static PATH_TO_CONTACT_FLOW_ID_STAGING: Record<string, string> = {
    "software/jira/product-discovery":
        "4cdb6e29-9088-4404-bbfe-dfdba35bd3d6",
    "software/jira":
        "7e776619-ede4-4975-a194-38c6b182fa0c",
    "software/loom":
        "42cee94e-c0db-43d2-bd45-3cca9d7adb61"
  };



  static getMacUrl() {
    if (this.isStaging() || this.isDev()) {
      return "https://my.stg.internal.atlassian.com";
    } else {
      return "https://my.atlassian.com";
    }
  }

  static getStartUrl() {
    if (this.isStaging() || this.isDev()) {
      return "https://start.stg.atlassian.com";
    } else {
      return "https://start.atlassian.com";
    }
  }

  static getAPIPrivateURL() {
    if (this.isStaging() || this.isDev()) {
      return "https://wac.stg.internal.atlassian.com/gateway/api";
    }

    return "https://www.atlassian.com/gateway/api";
  }

  static getMyAdminAtlasBaseURL() {
    if (this.isStaging() || this.isDev()) {
      return "https://admin.stg.atlassian.com";
    }

    return "https://admin.atlassian.com";
  }

  static getBaseURL() {
    if (this.isStaging() || this.isDev()) {
      return "https://wac.stg.internal.atlassian.com";
    }

    return "https://www.atlassian.com";
  }

  static getWacIntegrationUrl() {
    if (this.isStaging() || this.isDev()) {
      const useQaWAC =
        window.location.hostname.indexOf("qa-wac") > -1 ||
        window.location.hostname.indexOf("author") > -1;
      return `${useQaWAC
        ? "https://qa-wac.internal.atlassian.com"
        : stgConfig.stgWacInternalUrl
        }`;
    } else {
      return "https://www.atlassian.com";
    }
  }

  static getProductTld = (): string => {
    return this.getEnvironment() === ENV_PRODUCTION ? '.atlassian.net' : '.jira-dev.com';
  };

  static getBBIntegrationUrl() {
    if (this.isStaging() || this.isDev()) {
      return "https://integration.bb-inf.net";
    } else {
      return "https://bitbucket.org";
    }
  }

  static getOmniChannelEnvironment() {
    const env = this.getEnvironment();
    return env === ENV_PRODUCTION ? 'prod' : 'stg';
  }

  static getOmniChannelFlowId() {
    const env = this.getEnvironment();
    // Return different flow IDs based on environment
    switch (env) {
      case ENV_PRODUCTION:
        return Environment.PATH_TO_CONTACT_FLOW_ID_PRODUCTION[Environment.getRelativePathFromWacUrl(window.location.href)] || 'ea0c4f06-322f-4ea5-9d6e-758ae3a86980'; 
      case ENV_STAGING:
        return Environment.PATH_TO_CONTACT_FLOW_ID_STAGING[Environment.getRelativePathFromWacUrl(window.location.href)] || 'ea0c4f06-322f-4ea5-9d6e-758ae3a86980';
      default:
        return 'ea0c4f06-322f-4ea5-9d6e-758ae3a86980'; // Dev flow ID (same for now)
    }
  }

  static getOmniChannelGraphQLUrl() {
    const env = this.getEnvironment();
    return env === ENV_PRODUCTION
      ? 'https://support.atlassian.com/gateway/api/graphql'
      : 'https://support.stg.internal.atlassian.com/gateway/api/graphql';
  }
}
